/**
 * ResultsContentLayout Component
 *
 * Two-column layout wrapper for the results page containing:
 * - Mobile "All Filters" fallback button
 * - Mobile sort header
 * - Mobile filters sheet
 * - Desktop headers row (Filters header + Sort controls)
 * - Content row (Filters sidebar + Product grid)
 */

import React from "react";

import Image from "next/image";

import { MobileAllFiltersButton } from "@/components/results/MobileAllFiltersButton";
import { MobileFiltersSheet } from "@/components/results/MobileFiltersSheet";
import { ResultsSortHeader } from "@/components/results/ResultsSortHeader";
import type { SortOrder } from "@/components/results/sort-utils";
import { CountBadge } from "@/components/ui/CountBadge";

export interface ResultsContentLayoutProps {
  /** Whether search is in progress */
  isSearching: boolean;
  /** Whether there are products to display */
  hasProducts: boolean;
  /** Total product count */
  productCount: number;
  /** Whether there's an active search (search params exist) */
  hasSearchParams: boolean;

  /** Whether the sticky filters bar is showing (to hide the fallback button) */
  showStickyFiltersBar: boolean;

  /** Mobile filters sheet state */
  isMobileFiltersOpen: boolean;
  /** Callback to change mobile filters state */
  onMobileFiltersOpenChange: (open: boolean) => void;
  /** Count of applied filters */
  appliedFiltersCount: number;
  /** Callback to clear all filters */
  onClearAllFilters: () => void;

  /** Current sort order */
  sortOrder: SortOrder;
  /** Whether sort menu is open */
  isSortMenuOpen: boolean;
  /** Callback to toggle sort menu */
  onToggleSortMenu: () => void;
  /** Callback when sort option is selected */
  onSortSelect: (sort: SortOrder) => void;
  /** ID for sort dropdown accessibility */
  sortDropdownListId: string;

  /** Children for the filters content (rendered in mobile sheet and desktop sidebar) */
  filtersContent: React.ReactNode;
  /** Children for the product grid area */
  children: React.ReactNode;
}

/**
 * Two-column layout for results page with filters sidebar and product grid
 */
export function ResultsContentLayout({
  isSearching,
  hasProducts,
  productCount,
  hasSearchParams,
  showStickyFiltersBar,
  isMobileFiltersOpen,
  onMobileFiltersOpenChange,
  appliedFiltersCount,
  onClearAllFilters,
  sortOrder,
  isSortMenuOpen,
  onToggleSortMenu,
  onSortSelect,
  sortDropdownListId,
  filtersContent,
  children,
}: ResultsContentLayoutProps): React.ReactElement {
  return (
    <div className="w-full px-4 pt-4 pb-24 sm:pb-0 lg:px-[30px] lg:pt-[30px] lg:pb-40">
      {/* Mobile "All Filters" - sticky fallback when no suggested filters section */}
      {!isSearching && !showStickyFiltersBar && (
        <div className="sticky top-[48px] z-40 -mx-4 mb-4 w-[calc(100%+32px)] bg-white px-4 py-2 lg:hidden">
          <MobileAllFiltersButton
            onClick={() => onMobileFiltersOpenChange(true)}
            filterCount={appliedFiltersCount}
          />
        </div>
      )}

      {/* Mobile Header - Showing + Sort (Filter button moved to sticky section) */}
      {!isSearching && hasSearchParams && hasProducts && (
        <ResultsSortHeader
          variant="mobile"
          productCount={productCount}
          sortOrder={sortOrder}
          isSortMenuOpen={isSortMenuOpen}
          onToggleSortMenu={onToggleSortMenu}
          onSortSelect={onSortSelect}
          sortDropdownListId={sortDropdownListId}
        />
      )}

      {/* Mobile Filters Sheet - controlled by sticky "All Filters" button */}
      <MobileFiltersSheet
        isOpen={isMobileFiltersOpen}
        onOpenChange={onMobileFiltersOpenChange}
        activeFilterCount={appliedFiltersCount}
        onClearAll={onClearAllFilters}
        hideTrigger
      >
        {filtersContent}
      </MobileFiltersSheet>

      {/* Desktop Headers Row - Filters + Showing + Sort By aligned */}
      <div className="hidden gap-[30px] lg:flex" style={{ alignItems: "baseline" }}>
        {/* Left: Filters Header */}
        {!isSearching && hasSearchParams && (
          <div style={{ width: "251.325px" }}>
            <div
              style={{
                display: "flex",
                gap: "14px",
                alignItems: "center",
                marginBottom: "7.993px",
              }}
            >
              <Image src="/icons/filters-icon.svg" alt="" width={13.987} height={13.987} />
              <span className="text-action text-ink">Filters</span>
              {appliedFiltersCount > 0 && (
                <CountBadge count={appliedFiltersCount} variant="desktop-header" />
              )}
            </div>
            <div
              style={{
                width: "100%",
                height: "0.882px",
                borderBottom: "0.882px solid rgba(0, 0, 0, 1)",
              }}
            />
          </div>
        )}

        {/* Right: Showing + Sort By Controls */}
        {!isSearching && hasSearchParams && hasProducts && (
          <ResultsSortHeader
            variant="desktop"
            productCount={productCount}
            sortOrder={sortOrder}
            isSortMenuOpen={isSortMenuOpen}
            onToggleSortMenu={onToggleSortMenu}
            onSortSelect={onSortSelect}
            sortDropdownListId={sortDropdownListId}
          />
        )}
      </div>

      {/* Content Row - Filters Content + Product Grid aligned */}
      <div className="mt-4 flex gap-4 lg:mt-[35px] lg:gap-[30px]">
        {/* Left: Filters Content - Desktop only */}
        {!isSearching && hasSearchParams && (
          <div className="hidden w-[251.325px] flex-shrink-0 lg:block">{filtersContent}</div>
        )}

        {/* Right: Product Grid */}
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      </div>
    </div>
  );
}
